import type { ApiClientResponse } from "@lobby/api-client";
import { FortuneWheel as FortuneWheelModel, Lobby } from "@lobby/core/src/entities";
import { APIError, emitter, useFortuneWheelCountdown } from "@lobby/core/src/shared";
import { useTranslate } from "@lobby/ocb-intl";
import { useQueryClient } from "@tanstack/react-query";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import {
  WHEEL_RADIUS as R,
  WHEEL_DIAMETER,
  WHEEL_INITIAL_VELOCITY,
  buildSectorPath,
  calcAcceleration,
  calcCircumference,
  calcDistance,
  calcPointX,
  calcPointY,
  calcSectorAngle,
  calcTotalRotation,
  getSectorColors,
  getSectorFilling,
  getWinSectorIndex,
  WHEEL_INNER_RADIUS as r,
} from "../lib";
import type { TSector, TSectorParams, TWheelState, TWinSector } from "../lib";
import { Timer } from "./timer";
import { WheelSector } from "./wheel-sector";

interface IFortuneWheelProps {
  className?: string;
  sectors: TSector[];
  onWinAnimationEnd?: (params: TSectorParams) => void;
}

export const FortuneWheel = memo(function FortuneWheel({
  className,
  sectors,
  onWinAnimationEnd,
}: IFortuneWheelProps) {
  const { $t } = useTranslate();
  const [wheelState, setWheelState] = useState<TWheelState>("idle");
  const [winnerSector, setWinnerSector] = useState<TWinSector>();
  const wheelRef = useRef<SVGGElement>(null);

  const queryClient = useQueryClient();
  const { mutate: spinWheel } = FortuneWheelModel.useWheelSpin();
  const { data: lobbyData } = Lobby.useLobby();

  const countdown = useFortuneWheelCountdown(lobbyData?.fortuneWheel?.nextSpinTime as number);

  const winSectorId = winnerSector?.sectorId;
  const winSectorIndex = useMemo(
    () => getWinSectorIndex(sectors, winSectorId),
    [sectors, winSectorId],
  );

  const sectorAngle = calcSectorAngle(sectors.length);
  const sectorPath = buildSectorPath(R, r, sectorAngle);
  const sectorTextX = calcPointX(R, r - 60, -90 - 2);

  const spinText = $t({ defaultMessage: "SPIN!" }).toUpperCase();

  function invalidateWheelState() {
    queryClient.invalidateQueries({ queryKey: ["Lobby.getCurrent"] });
  }

  function handleSpinMutationSuccess({ result, error }: ApiClientResponse<"FortuneWheel.getWin">) {
    if (result) {
      setWheelState("spinning");
      setWinnerSector(result);
    }

    if (error) {
      setWheelState("ready");
      invalidateWheelState();

      const apiError = new APIError(error.message, {
        code: error.code,
      });

      emitter.emit("ERROR", { error: apiError });
    }
  }

  function handleSpinClick() {
    setWheelState("fetching");
    spinWheel(undefined, { onSuccess: handleSpinMutationSuccess });
  }

  useEffect(() => {
    if (wheelState === "win" && winnerSector && onWinAnimationEnd) {
      setTimeout(
        () =>
          onWinAnimationEnd({
            id: winnerSector.sectorId,
            value: winnerSector.win,
            colors: getSectorColors(winSectorIndex),
          }),
        2000,
      );
    }
  }, [wheelState, winSectorIndex, winnerSector]);

  useEffect(() => {
    if (wheelState !== "spinning" || wheelRef.current === null) {
      return;
    }

    const wheel = wheelRef.current;
    const circumference = calcCircumference(R);
    const totalRotation = calcTotalRotation(sectors.length, winSectorIndex);
    const totalDistance = (totalRotation / 360) * circumference;
    const initialVelocity = WHEEL_INITIAL_VELOCITY;
    const acceleration = calcAcceleration(initialVelocity, totalDistance);

    let lastDistance = 0;
    const startTime = Number(document.timeline?.currentTime) || 0;

    function animate(time: DOMHighResTimeStamp) {
      const elapsed = time - startTime;

      const distance = calcDistance(initialVelocity, elapsed, acceleration);
      if (lastDistance > distance) {
        setWheelState("win");
        return;
      }
      lastDistance = distance;

      const angle = (distance / circumference) * 360;

      wheel.setAttribute("transform", `rotate(${angle} ${R} ${R})`);

      requestAnimationFrame(animate);
    }

    const requestId = requestAnimationFrame(animate);

    () => cancelAnimationFrame(requestId);
  }, [wheelState]);

  useEffect(() => {
    setWheelState(countdown > 0 ? "idle" : "ready");
  }, [countdown]);

  return (
    <div style={{ direction: "ltr" }} className={className}>
      <svg
        className="select-none"
        xmlns="http://www.w3.org/2000/svg"
        fill="none"
        width="100%"
        height="100%"
        viewBox={`0 0 ${WHEEL_DIAMETER} ${WHEEL_DIAMETER}`}
        textRendering="optimizeSpeed"
        colorRendering="optimizeSpeed"
      >
        <circle className="fill-java/30 stroke-java" cx="50%" cy="50%" r="50%" strokeWidth="3" />
        <circle cx="50%" cy="50%" r="48%" fill="#2e3641" />
        <circle cx="50%" cy="50%" r="46%" stroke="#546B81" />

        {/* Sectors group */}
        <g ref={wheelRef}>
          {/* Sectors */}
          {sectors.map(({ id }, idx) => (
            <WheelSector
              key={idx}
              path={sectorPath}
              fill={getSectorFilling(idx)}
              transform={`rotate(${idx * sectorAngle} ${R} ${R})`}
              isWinSector={id === winSectorId && wheelState === "win"}
            />
          ))}

          {/* Sectors text */}
          {sectors.map(({ value }, idx) => {
            value = value / 100;
            return (
              <g transform={`rotate(${idx * sectorAngle} ${R} ${R})`} key={idx}>
                {value
                  .toString()
                  .split("")
                  .map((char, idx) => (
                    <text
                      className="fill-white font-commissioner font-extrabold text-4xl tracking-[1rem] [text-shadow:0_0_16px_black]"
                      x={sectorTextX}
                      y={calcPointY(R, r - 60 - idx * 40, -105)}
                      rotate="6"
                      key={idx}
                    >
                      {char}
                    </text>
                  ))}
              </g>
            );
          })}
        </g>

        <circle cx="50%" cy="50%" r={r} stroke="url(#linGrad001)" strokeWidth="6" />
        <circle cx="50%" cy="50%" r="15.5%" fill="url(#linGrad005)" />
        <circle
          cx="50%"
          cy="50%"
          r="16.1%"
          stroke="url(#linGrad006)"
          filter="url(#filter003)"
          strokeWidth="13"
        />
        <circle cx="50%" cy="50%" r="10%" stroke="url(#linGrad007)" />
        <circle cx="50%" cy="50%" r="11%" stroke="url(#linGrad007)" />
        <circle cx="50%" cy="50%" r="12%" stroke="url(#linGrad007)" />
        <circle cx="50%" cy="50%" r="13%" stroke="url(#linGrad007)" />
        <circle
          cx="50%"
          cy="50%"
          r="7%"
          stroke="#13C7BC"
          strokeWidth="3"
          filter="url(#filter004)"
          fill="none"
        />

        {wheelState === "idle" ? (
          <Timer />
        ) : (
          <g className="pointer-events-none">
            {/* Fire on ball */}
            <g transform={`translate(${R - 113} ${R - 64})`}>
              <mask id="path-1-inside-1_4686_2406" fill="#fff">
                <path
                  fillRule="evenodd"
                  d="M166.66 31.16c18.08 31.32 7.67 71.18-23.25 89.04a64.61 64.61 0 0 1-5.88 3 83.17 83.17 0 0 1-17.56 13.44c-8.88 5.12-21.2 9.97-29.9 9.85-.36 0-.44-.44-.14-.65 3.5-2.35 5.73-5.74 6.45-8.24l.02-.06c1.22-4.22 1.83-6.36 1.87-10.12-.65-.14-1.3-.28-1.94-.44-2.09 1.75-3.8 4.44-5.68 7.38-.42.66-.85 1.33-1.3 2-3.6 5.54-12.12 10.42-18 10.53l-2.19.03c-1.52.01-3.04.02-4.57.11-.75.05-1.56.07-2.41.1-4.2.11-9.27.26-12.32 3.07-.25.22-.64.05-.6-.26a11.56 11.56 0 0 1 4.23-7.85c2.3-1.87 4.92-3.04 7.6-4.23l1.66-.75c5.65-2.59 8.4-7.9 9.59-13.64.07-.36-.4-.53-.64-.24-5 5.7-12.85 9.3-20.18 8.43-1.5-.18-3.88-.6-6.2-1.01-1.91-.34-3.77-.67-5-.84-15.79-2.14-30.14 6.83-39.09 17.85-.23.28-.67.14-.64-.2 1.3-14.91 5.97-29.5 17.55-40.35 4-3.76 8.05-5.68 11.75-7.44 6.99-3.31 12.78-6.06 14.87-19.45-4.55 2.5-10.52 2.58-15.68 1.53-.46-.09-.6-.6-.2-.88 3.42-2.33 5.81-4.18 7.49-8.68.82-2.19 1.46-4.43 2.1-6.66.98-3.48 1.97-6.95 3.64-10.23 1.58-3.1 7.1-6.38 9.31-9.03 4.65-12.7 14.71-27.96 30.3-36.96 27.43-15.83 66.85-9.47 84.94 21.85Z"
                  clipRule="evenodd"
                />
              </mask>
              <path
                fill="url(#paint0_linear_4686_2406)"
                d="M166.66 31.16c18.08 31.32 7.67 71.18-23.25 89.04a64.61 64.61 0 0 1-5.88 3 83.17 83.17 0 0 1-17.56 13.44c-8.88 5.12-21.2 9.97-29.9 9.85-.36 0-.44-.44-.14-.65 3.5-2.35 5.73-5.74 6.45-8.24l.02-.06c1.22-4.22 1.83-6.36 1.87-10.12-.65-.14-1.3-.28-1.94-.44-2.09 1.75-3.8 4.44-5.68 7.38-.42.66-.85 1.33-1.3 2-3.6 5.54-12.12 10.42-18 10.53l-2.19.03c-1.52.01-3.04.02-4.57.11-.75.05-1.56.07-2.41.1-4.2.11-9.27.26-12.32 3.07-.25.22-.64.05-.6-.26a11.56 11.56 0 0 1 4.23-7.85c2.3-1.87 4.92-3.04 7.6-4.23l1.66-.75c5.65-2.59 8.4-7.9 9.59-13.64.07-.36-.4-.53-.64-.24-5 5.7-12.85 9.3-20.18 8.43-1.5-.18-3.88-.6-6.2-1.01-1.91-.34-3.77-.67-5-.84-15.79-2.14-30.14 6.83-39.09 17.85-.23.28-.67.14-.64-.2 1.3-14.91 5.97-29.5 17.55-40.35 4-3.76 8.05-5.68 11.75-7.44 6.99-3.31 12.78-6.06 14.87-19.45-4.55 2.5-10.52 2.58-15.68 1.53-.46-.09-.6-.6-.2-.88 3.42-2.33 5.81-4.18 7.49-8.68.82-2.19 1.46-4.43 2.1-6.66.98-3.48 1.97-6.95 3.64-10.23 1.58-3.1 7.1-6.38 9.31-9.03 4.65-12.7 14.71-27.96 30.3-36.96 27.43-15.83 66.85-9.47 84.94 21.85Z"
              />
              <path
                fill="#13c7bc"
                filter="url(#filter006)"
                d="m143.4 120.2 1.5 2.6-1.5-2.6Zm-5.87 3-2.13-2.11.4-.4.5-.23 1.23 2.74Zm-17.56 13.44 1.5 2.6-1.5-2.6Zm-29.9 9.85.03-3-.03 3Zm-.14-.65-1.68-2.48 1.68 2.48Zm6.45-8.24 2.88.84-2.88-.84Zm.02-.06 2.88.83-2.88-.83Zm1.87-10.12.61-2.94 2.42.5-.03 2.47-3-.03Zm-1.94-.44-1.93-2.3 1.16-.97 1.47.36-.7 2.91Zm-5.68 7.38-2.53-1.61 2.53 1.61Zm-1.3 2-2.5-1.63 2.5 1.64Zm-18 10.53-.07-3 .06 3Zm-2.19.03.03 3-.03-3Zm-4.57.11.18 3-.18-3Zm-2.41.1.08 3-.08-3Zm-12.32 3.07 2.03 2.2v.01h-.01l-2.02-2.21Zm-.6-.26 2.98.37-2.98-.37Zm4.23-7.85-1.9-2.32 1.9 2.32Zm7.6-4.23 1.22 2.74-1.22-2.74Zm1.66-.75-1.25-2.73 1.25 2.73Zm9.59-13.64 2.94.57v.03l-2.94-.6Zm-.64-.24 2.25 1.98-2.25-1.98Zm-20.18 8.43-.36 2.97.36-2.97Zm-6.2-1.01-.51 2.95.52-2.95Zm-5-.84.4-2.97-.4 2.97ZM1.23 147.66l2.33 1.89-2.33-1.9Zm-.64-.2-3-.26 3 .26Zm17.55-40.35-2.05-2.2 2.05 2.2Zm11.75-7.44-1.28-2.7 1.28 2.7Zm14.87-19.45L43.3 77.6l5.35-2.95-.94 6.04-2.96-.47Zm-15.68 1.53.58-2.94h.02l-.6 2.94Zm-.2-.88-1.7-2.47h.01v-.01l1.7 2.48Zm7.49-8.68-2.81-1.05 2.81 1.05Zm2.1-6.66 2.88.82-2.89-.82ZM42.1 55.3l-2.68-1.36 1.05-2.07 2.27.5-.64 2.93Zm0 0 2.68 1.36-1.05 2.07-2.27-.5.65-2.93Zm9.31-9.03 2.82 1.03-.18.49-.33.4-2.3-1.92ZM81.72 9.3l1.5 2.6-1.5-2.6Zm87.53 20.35c18.9 32.73 8.05 74.44-24.34 93.13l-3-5.2c29.45-17 39.42-55.01 22.15-84.93l5.2-3Zm-24.34 93.13a67.53 67.53 0 0 1-6.15 3.15l-2.46-5.48a61.7 61.7 0 0 0 5.6-2.86l3 5.2Zm-5.25 2.52a86.2 86.2 0 0 1-18.19 13.92l-3-5.2a80.17 80.17 0 0 0 16.93-12.94l4.26 4.22Zm-18.19 13.92a93.19 93.19 0 0 1-15.53 7.13c-5.43 1.9-11.07 3.2-15.92 3.13l.09-6c3.85.05 8.72-1 13.85-2.8a87.27 87.27 0 0 0 14.51-6.65l3 5.2ZM90.03 149.5a3.3 3.3 0 0 1-3.22-2.66c-.27-1.34.31-2.7 1.44-3.47l3.36 4.97c.83-.56 1.3-1.6 1.09-2.68a2.7 2.7 0 0 0-2.6-2.16l-.07 6Zm-1.78-6.13c2.96-2 4.74-4.82 5.25-6.59l5.76 1.67c-.93 3.22-3.62 7.17-7.65 9.9l-3.36-4.98Zm5.25-6.59.02-.06 5.76 1.66-.02.07-5.76-1.67Zm.02-.06c1.21-4.22 1.72-6 1.75-9.32l6 .06c-.04 4.2-.77 6.7-1.99 10.92l-5.76-1.66Zm4.14-6.36a70.5 70.5 0 0 1-2.03-.45l1.4-5.84c.61.15 1.23.3 1.85.42l-1.22 5.87Zm.6-1.07c-1.66 1.39-3.13 3.63-5.08 6.7l-5.06-3.23c1.8-2.82 3.77-5.96 6.28-8.07l3.85 4.6Zm-5.08 6.7L91.87 138l-5.02-3.28 1.27-1.98 5.06 3.22ZM91.87 138c-2.14 3.27-5.58 6.17-9.16 8.25-3.57 2.07-7.68 3.56-11.31 3.63l-.12-6c2.25-.04 5.35-1.04 8.41-2.82 3.05-1.77 5.68-4.08 7.16-6.34l5.02 3.28ZM71.4 149.89l-2.21.03-.05-6c.73 0 1.43-.01 2.14-.03l.12 6Zm-2.21.03c-1.54.01-2.98.02-4.42.1l-.35-5.98c1.6-.1 3.2-.11 4.72-.12l.05 6Zm-4.42.1c-.8.05-1.67.08-2.5.1l-.18-6c.86-.02 1.63-.04 2.33-.08l.35 5.99Zm-2.5.1c-2.15.06-4.23.13-6.14.48-1.9.36-3.3.94-4.24 1.8l-4.06-4.4c2.1-1.95 4.77-2.84 7.21-3.3 2.44-.45 5-.52 7.05-.57l.17 6Zm-10.4 2.3a3.38 3.38 0 0 1-3.6.6 3.3 3.3 0 0 1-1.98-3.45l5.95.75a2.7 2.7 0 0 0-1.6-2.8c-.9-.39-2-.27-2.8.46l4.04 4.44Zm-5.58-2.85a14.55 14.55 0 0 1 5.3-9.8l3.8 4.64a8.56 8.56 0 0 0-3.15 5.9l-5.95-.74Zm5.3-9.8c2.66-2.17 5.66-3.49 8.28-4.65l2.44 5.48c-2.74 1.22-4.98 2.23-6.92 3.81l-3.8-4.65Zm8.28-4.65 1.63-.74 2.5 5.46-1.69.76-2.44-5.48Zm1.63-.74c4.43-2.03 6.82-6.25 7.9-11.52l5.88 1.21C74 130.3 70.87 136.7 64 139.84l-2.5-5.46Zm7.9-11.48a2.68 2.68 0 0 0 1.7 3.02c1 .37 2.14.1 2.85-.71l-4.51-3.96a3.4 3.4 0 0 1 3.73-.96 3.3 3.3 0 0 1 2.11 3.75l-5.89-1.14Zm4.55 2.31c-5.53 6.3-14.3 10.44-22.79 9.42l.71-5.95c6.18.74 13.1-2.33 17.57-7.43l4.51 3.96Zm-22.79 9.42c-1.6-.19-4.09-.63-6.35-1.03l1.04-5.9c2.35.4 4.64.81 6.02.98l-.71 5.95Zm-6.35-1.03c-1.95-.34-3.74-.66-4.9-.82l.81-5.94c1.3.18 3.24.52 5.13.85l-1.04 5.91Zm-4.9-.82c-14.37-1.95-27.78 6.23-36.35 16.77l-4.66-3.78c9.34-11.5 24.63-21.27 41.82-18.93l-.8 5.94ZM3.56 149.55a3.4 3.4 0 0 1-3.63 1.1 3.31 3.31 0 0 1-2.33-3.45l5.98.51a2.7 2.7 0 0 0-1.88-2.78c-.98-.3-2.1-.02-2.8.83l4.66 3.8Zm-5.96-2.35c1.34-15.33 6.18-30.73 18.49-42.28l4.1 4.38C9.35 119.47 4.85 133.22 3.58 147.72l-5.98-.52Zm18.49-42.28c4.38-4.1 8.81-6.2 12.52-7.96l2.57 5.43c-3.7 1.75-7.36 3.5-10.99 6.9l-4.1-4.37Zm12.52-7.96c3.57-1.7 6.23-2.99 8.36-5.28 2.05-2.2 3.84-5.62 4.82-11.92l5.93.93c-1.1 7.08-3.25 11.74-6.36 15.08-3.04 3.26-6.77 5-10.18 6.62l-2.57-5.43Zm17.6-14.1c-5.43 2.98-12.23 2.95-17.73 1.83l1.2-5.88c4.82.98 9.97.8 13.63-1.21l2.9 5.25Zm-17.72 1.83a3.48 3.48 0 0 1-1.31-6.3l3.4 4.95a2.52 2.52 0 0 0 1-2.63 2.5 2.5 0 0 0-1.92-1.9l-1.17 5.88Zm-1.3-6.3c1.71-1.16 2.92-2.05 3.9-3.1.94-1 1.76-2.24 2.47-4.15l5.62 2.1a17.75 17.75 0 0 1-3.71 6.16 28.2 28.2 0 0 1-4.9 3.95l-3.37-4.96Zm6.37-7.25a84.32 84.32 0 0 0 2.02-6.43l5.77 1.64c-.63 2.22-1.3 4.57-2.17 6.9l-5.62-2.1Zm2.02-6.43c.97-3.44 2.03-7.2 3.85-10.77l5.35 2.72c-1.51 2.97-2.43 6.17-3.43 9.69l-5.77-1.64Zm7.17-12.34h.01l-1.3 5.86 1.3-5.86Zm-3.3 1.57c1.1-2.2 3.4-4.17 5.17-5.63 2.05-1.7 3.63-2.91 4.5-3.96l4.6 3.84c-1.33 1.6-3.62 3.36-5.27 4.74-1.95 1.61-3.2 2.83-3.66 3.73l-5.35-2.72Zm9.15-8.7c4.83-13.19 15.28-29.1 31.62-38.53l3 5.2c-14.84 8.57-24.51 23.18-28.98 35.4l-5.64-2.07ZM80.22 6.7c28.67-16.55 70.02-9.99 89.03 22.95l-5.2 3C146.92 2.96 109.42-3.2 83.23 11.91l-3-5.2Z"
                mask="url(#path-1-inside-1_4686_2406)"
              />
              <defs>
                <linearGradient
                  id="paint0_linear_4686_2406"
                  x1="41.56"
                  x2="195.52"
                  y1="91.89"
                  y2="-.57"
                  gradientUnits="userSpaceOnUse"
                >
                  <stop stopColor="#2E3641" />
                  <stop offset="1" stopColor="#13C7BC" />
                </linearGradient>
              </defs>
            </g>

            {/* Glow around the ball  */}
            <circle
              className={`peer cursor-pointer ${wheelState === "ready" ? "pointer-events-auto" : "pointer-events-none"}`}
              cx="50%"
              cy="50%"
              r="63"
              fill="#13c7bc"
              fillOpacity="0.4"
              filter="url(#filter007)"
              onClick={handleSpinClick}
            />
            <circle
              className="peer-hover:invisible"
              cx="50%"
              cy="50%"
              r="50"
              fill="#252c38"
              stroke="#13c7bc"
              strokeWidth="3"
              filter="url(#filter004)"
            />

            {/* Ball */}
            <g className="peer-active:invisible" transform={`translate(${R - 50} ${R - 50})`}>
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M6.29 47.13c.3-4.69 1.38-9.18 3.05-13.36l11.28-14 2.83-4.54c7.4-5.64 16.57-9 26.55-9h.44l5 1.84 11.18 1.42a43.87 43.87 0 0 1 16.79 12.22l-1.8 7.75 7.9 22.26 4.23 1.05a43.78 43.78 0 0 1-13 28.37l3.83-20.5-23.4-2.9-17.13 19.27L57.9 91.44l21.8-9.3A43.64 43.64 0 0 1 50 93.77a43.55 43.55 0 0 1-28.92-10.95l8.27-7.1-9.23-23.8-13.83-4.79ZM52.27 7.32l17.71 16.51-14.5 20.74-26.62-5.02-.83-23.52 24.24-8.71Z"
              >
                {(wheelState === "fetching" ||
                  wheelState === "spinning" ||
                  wheelState === "win") && (
                  <animateTransform
                    id="fw-ballSpin"
                    attributeName="transform"
                    type="rotate"
                    from="0 50 50"
                    to="360 50 50"
                    dur="4s"
                    repeatCount={wheelState === "win" ? "1" : "indefinite"}
                  />
                )}
              </path>
            </g>

            {/* Second ball */}
            <g
              className="invisible peer-active:visible"
              transform={`translate(${R - 50 * 0.9} ${R - 50 * 0.9}) scale(0.9)`}
            >
              <path
                fill="#fff"
                fillRule="evenodd"
                d="M6.29 47.13c.3-4.69 1.38-9.18 3.05-13.36l11.28-14 2.83-4.54c7.4-5.64 16.57-9 26.55-9h.44l5 1.84 11.18 1.42a43.87 43.87 0 0 1 16.79 12.22l-1.8 7.75 7.9 22.26 4.23 1.05a43.78 43.78 0 0 1-13 28.37l3.83-20.5-23.4-2.9-17.13 19.27L57.9 91.44l21.8-9.3A43.64 43.64 0 0 1 50 93.77a43.55 43.55 0 0 1-28.92-10.95l8.27-7.1-9.23-23.8-13.83-4.79ZM52.27 7.32l17.71 16.51-14.5 20.74-26.62-5.02-.83-23.52 24.24-8.71Z"
              />
            </g>
            {wheelState === "ready" && (
              // Spin text
              <g>
                <path
                  id="path001"
                  fill="none"
                  d={`M${R} ${R - 80}a 50 50 0 0 1 0 160a 50 50 0 0 1 0 -160`}
                  transform={`rotate(${(spinText.length / 2) * -7} ${R} ${R})`}
                />
                <g>
                  <text
                    className="fill-java stroke-java font-bold font-commissioner [text-shadow:0_0_24px_#13c7bc]"
                    strokeWidth={4}
                    strokeLinejoin="round"
                    fontSize={40}
                  >
                    <textPath href="#path001" startOffset="0%">
                      {spinText}
                      <animate
                        attributeName="startOffset"
                        values="-100%;0%;"
                        fill="freeze"
                        dur="1s"
                        repeatCount="1"
                      />
                    </textPath>
                  </text>
                  <text className="fill-white font-bold font-commissioner" fontSize={40}>
                    <textPath href="#path001" startOffset="0%">
                      {spinText}
                      <animate
                        attributeName="startOffset"
                        values="-100%;0%;"
                        fill="freeze"
                        dur="1s"
                        repeatCount="1"
                      />
                    </textPath>
                  </text>
                </g>
              </g>
            )}
          </g>
        )}

        <g transform={`translate(${R - 53.5} -50)`}>
          {/* Arrow */}
          <path
            fill="#2e3641"
            filter={wheelState !== "idle" ? "url(#filter005)" : undefined}
            stroke="#13c7bc"
            strokeWidth="6"
            d="M57.22 123.9a4 4 0 0 1-7.44 0L4.15 8.99A4 4 0 0 1 7.86 3.5h91.28A4 4 0 0 1 102.85 9L57.22 123.9Z"
          />

          {/* Fire on arrow */}
          {wheelState === "win" && (
            <g filter="url(#filter008)" transform="translate(-18 -70)">
              <path
                fill="url(#radGrad001)"
                d="M67.24 154.099a28.95 28.95 0 0 0 8.684-5.345c.877-.783 2.819-4.283 2.819-4.283s6.371-1.813 9.46-4.571c4.04-3.606 8.38-9.129 9.471-13.83.044-.19-.188-.333-.34-.218-1.78 1.341-3.962 1.77-5.45 1.59l-.038-.004c-2.513-.306-3.787-.461-5.888-1.298.011-.38.017-.762.017-1.144 1.25-.724 2.978-1.033 4.865-1.371.424-.076.855-.154 1.291-.237 3.565-.684 7.414-4.152 8.261-7.289.104-.387.204-.775.304-1.162l.001-.002c.21-.815.42-1.63.673-2.429.124-.394.245-.825.372-1.275v-.002c.626-2.229 1.382-4.923 3.354-5.922.157-.081.115-.329-.064-.379-1.755-.49-3.414-.42-4.938.488-1.35.807-2.347 1.949-3.37 3.118v.001c-.21.24-.42.481-.636.721-2.194 2.449-5.517 2.723-8.877 2.049-.21-.044-.24-.33-.047-.399 3.84-1.393 6.893-4.786 7.382-8.927.098-.841.181-2.223.261-3.556.067-1.108.131-2.183.201-2.884.908-8.97 7.82-14.64 15.147-16.943.187-.059.17-.33-.025-.392-8.287-.15-12.653-.79-15.489-1.206-3.926-.575-4.918-.72-9.327 1.458-2.624 1.297-4.23 3.033-5.705 4.624-2.776 3-5.08 5.487-12.815 3.562 2-1.872 2.838-5.064 2.94-8.08.01-.267-.262-.455-.467-.305-1.75 1.31-3.101 2.171-5.83 2.049-1.329-.059-2.66-.226-3.991-.393-2.07-.26-4.136-.518-6.183-.369v.006c-1.15.084-2.518.9-3.811 1.672-.887.529-1.738 1.037-2.46 1.273a38.924 38.924 0 0 0-5.22.092 43.511 43.511 0 0 1 6.663-5.597c2.655-3.812 5.96-7.465 9.953-10.825 5.22-4.392 12.72-9.012 18.406-9.974.23-.04.332.234.16.401-2.017 1.951-3.094 4.415-3.287 6.123l-.005.044c-.323 2.885-.487 4.35-.09 6.797.438.01.877.027 1.316.05 1.166-1.383 1.984-3.336 2.879-5.47.2-.479.405-.966.618-1.458 1.74-4.028 6.752-8.21 10.577-8.988.472-.096.945-.187 1.417-.278.992-.19 1.984-.381 2.968-.622.486-.118 1.013-.23 1.563-.348h.002c2.725-.58 6.018-1.28 7.692-3.47.136-.175.409-.112.418.095.092 2.033-.433 3.921-1.884 5.606-1.287 1.49-2.865 2.561-4.481 3.657h-.002c-.33.226-.664.452-.996.682-3.399 2.357-4.604 6.132-4.733 10.007-.005.241.314.293.446.078 2.628-4.299 7.34-7.578 12.221-7.886.992-.062 2.599-.074 4.15-.085 1.289-.009 2.539-.018 3.362-.055 10.533-.496 18.892-8.042 23.5-16.266.118-.21.424-.174.443.053.812 9.837-.605 19.865-6.945 28.303-2.191 2.916-4.614 4.645-6.836 6.231-4.185 2.988-7.657 5.467-7.523 14.41 2.684-2.171 6.571-2.934 10.058-2.869.31.004.452.327.224.551-1.971 1.918-3.322 3.408-3.914 6.529-.289 1.52-.454 3.05-.62 4.579-.258 2.377-.515 4.75-1.235 7.077h-.006c-.405 1.307-1.709 2.819-2.941 4.248-.845.98-1.656 1.921-2.12 2.73-1.616 8.801-6.475 19.914-15.638 27.623-8.337 7.014-19.43 10.557-30.312 9.829Z"
              />
            </g>
          )}
        </g>

        <defs>
          <linearGradient
            id="linGrad001"
            x1="50%"
            y1="0%"
            x2="50%"
            y2="100%"
            gradientTransform="rotate(0)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#13C7BC" />
            <stop offset=".5" stopColor="#00FFF0" />
            <stop offset="1" stopColor="#09615B" />
            <animateTransform
              attributeName="gradientTransform"
              type="rotate"
              from={`0 ${R} ${R}`}
              to={`360 ${R} ${R}`}
              dur="4s"
              repeatCount="indefinite"
            />
          </linearGradient>
          <linearGradient id="linGrad003" gradientTransform="rotate(90)">
            <stop stopColor="#5D5D5D" />
            <stop offset=".24" stopColor="#FDFBEF" />
            <stop offset=".32" stopColor="#E6E2D3" />
            <stop offset=".4" stopColor="#DED9CC" />
            <stop offset=".47" stopColor="#D1CEC8" />
            <stop offset=".51" stopColor="#C7C1B7" />
            <stop offset=".55" stopColor="#CDCDCD" />
            <stop offset=".59" stopColor="#D2D2D2" />
            <stop offset=".64" stopColor="#DCDCDC" />
            <stop offset=".68" stopColor="#EAE7DF" />
            <stop offset=".72" stopColor="#fff" />
            <stop offset=".77" stopColor="#EDEDED" />
            <stop offset=".9" stopColor="#8E8E8E" />
            <stop offset="1" stopColor="#727272" />
          </linearGradient>
          <linearGradient id="linGrad005" gradientTransform="rotate(90)">
            <stop stopColor="#2e3641" />
            <stop offset={1} stopColor="#252d3a" />
          </linearGradient>
          <linearGradient id="linGrad006" gradientTransform="rotate(90)">
            <stop stopColor="#13C7BC" />
            <stop offset=".5" stopColor="#A2FFF9" />
            <stop offset="1" stopColor="#09615B" />
          </linearGradient>
          <linearGradient
            id="linGrad007"
            x1="50%"
            y1="40%"
            x2="50%"
            y2="60%"
            gradientTransform="rotate(0)"
            gradientUnits="userSpaceOnUse"
          >
            <stop stopColor="#2E3641" />
            <stop offset=".5" stopColor="#13C7BC" />
            <stop offset="1" stopColor="#2E3641" />
            <animateTransform
              attributeName="gradientTransform"
              type="rotate"
              from={`0 ${R} ${R}`}
              to={`360 ${R} ${R}`}
              dur="5s"
              repeatCount="indefinite"
            />
          </linearGradient>
          <radialGradient id="radGrad001" cx="0.85" cy="0.05" r="1">
            <stop stopColor="#f9df7b" />
            <stop offset=".4" stopColor="#ff002e" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="radGrad-blue" gradientUnits="userSpaceOnUse" cx={R} cy={R} r={R}>
            <stop stopColor="#0056a4" />
            <stop offset="0.65" stopColor="#0066b7" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="radGrad-gray" gradientUnits="userSpaceOnUse" cx={R} cy={R} r={R}>
            <stop stopColor="#262c35" />
            <stop offset="0.65" stopColor="#333a47" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="radGrad-green" gradientUnits="userSpaceOnUse" cx={R} cy={R} r={R}>
            <stop stopColor="#0c9f94" />
            <stop offset="0.65" stopColor="#11b6ab" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="radGrad-orange" gradientUnits="userSpaceOnUse" cx={R} cy={R} r={R}>
            <stop stopColor="#c16617" />
            <stop offset="0.65" stopColor="#d56e1f" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="radGrad-purple" gradientUnits="userSpaceOnUse" cx={R} cy={R} r={R}>
            <stop stopColor="#4b37b4" />
            <stop offset="0.65" stopColor="#664bca" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <radialGradient id="radGrad-red" gradientUnits="userSpaceOnUse" cx={R} cy={R} r={R}>
            <stop stopColor="#dd3050" />
            <stop offset="0.65" stopColor="#eb3455" />
            <stop offset="1" stopColor="#000" />
          </radialGradient>
          <filter id="filter003" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="0" stdDeviation="22" floodColor="#000" floodOpacity="0.65" />
          </filter>
          <filter id="filter004" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="0" stdDeviation="9" floodColor="#13c7bc" />
          </filter>
          <filter id="filter005" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="4" stdDeviation="12" floodColor="#13c7bc">
              <animate
                attributeName="flood-opacity"
                values="0;1;0"
                dur="3s"
                repeatCount="indefinite"
              />
            </feDropShadow>
          </filter>
          <filter id="filter006" filterUnits="userSpaceOnUse">
            <feDropShadow dx="0" dy="0" stdDeviation="8" floodColor="#000" floodOpacity="0.75" />
          </filter>
          <filter id="filter007" filterUnits="userSpaceOnUse">
            <feGaussianBlur in="SourceGraphic" stdDeviation="22" />
          </filter>
          <filter id="filter008" filterUnits="userSpaceOnUse">
            <feDropShadow dx="3" dy="0" stdDeviation="22" floodColor="#ff385c" floodOpacity="1">
              <animate
                attributeName="flood-opacity"
                values="0;1;0"
                dur="1s"
                repeatCount="indefinite"
              />
            </feDropShadow>
          </filter>
        </defs>
      </svg>
    </div>
  );
});
